import { Session } from "./game/session.js";
import { createState, isMyTurn, isPlaceholderNick, randomNickname, remainingMs, serverNow } from "./game/state.js";
import { clearResume, loadResume, loadStay } from "./game/resume.js";
import { GameSocket } from "./net/socket.js";
import { createBrowserPlatform } from "./platform/browser.js";
import type { Platform } from "./platform/types.js";
import { createWechatPlatform, isWechatRuntime } from "./platform/wechat.js";
import { loadAudioPrefs } from "./audio/prefs.js";
import { SFX } from "./audio/sfx.js";
import { loadAssets, type AssetBank } from "./render/assets.js";
import { drawDebug } from "./render/debug.js";
import { battleBands, collectButtons, drawFrame } from "./render/draw.js";
import { resultLayout } from "./render/screens/result.js";
import { hallHomeLayout, hallRankRows, handleUiTap, hitButton, layoutLogin } from "./ui/hall.js";
import {
  handleCandidateTap,
  keyboardEnabled,
  onKeyDown,
  onKeyMove,
  onKeyUp,
} from "./ui/keyboard.js";

function pickPlatform(): Platform {
  if (isWechatRuntime()) return createWechatPlatform();
  return createBrowserPlatform();
}

function boot(): void {
  const platform = pickPlatform();
  const saved = platform.getStorage("gaoya.nickname");
  const savedAvatar = platform.getStorage("gaoya.avatar") || "";
  const wechat = platform.kind === "wechat";
  const nick =
    saved && saved.length >= 1 && saved.length <= 16 && !(wechat && isPlaceholderNick(saved))
      ? saved
      : wechat
        ? "微信昵称"
        : randomNickname();
  const ui = createState(nick);
  ui.screen = "login";
  if (typeof location !== "undefined" && /[?&#]honor=1(?:&|$)/.test(location.href)) {
    ui.screen = "hall";
    ui.showLeaderboard = true;
  }
  ui.useWxProfile = wechat;
  const savedWins = Number(platform.getStorage("gaoya.wins") || 0);
  if (Number.isFinite(savedWins) && savedWins > 0) ui.totalWins = Math.floor(savedWins);
  ui.privacyAgreed = !wechat || platform.getStorage("gaoya.privacyAgreed") === "1";
  if (wechat && !ui.privacyAgreed) ui.showPrivacy = true;
  const prefs = loadAudioPrefs((key) => platform.getStorage(key));
  ui.bgmMuted = prefs.bgmMuted;
  ui.sfxMuted = prefs.sfxMuted;
  ui.vibrateMuted = prefs.vibrateMuted;
  const inset0 = platform.safeInset();
  ui.safeTop = inset0.top;
  ui.safeBottom = inset0.bottom;
  ui.capsuleLeft = inset0.capsuleLeft;
  ui.capsuleTop = inset0.capsuleTop;
  ui.capsuleHeight = inset0.capsuleHeight;
  platform.applyAudioPrefs();
  if (wechat && saved && !isPlaceholderNick(saved)) {
    ui.avatarUrl = savedAvatar;
    ui.wxProfileReady = true;
  }
  const stay = loadStay((key) => platform.getStorage(key));
  const resume = stay === "room" ? loadResume((key) => platform.getStorage(key)) : null;
  if (stay !== "room") {
    clearResume((key, value) => platform.setStorage(key, value));
  }
  if (resume) {
    ui.token = resume.token;
    ui.roomId = resume.roomId;
    ui.userId = resume.userId;
  }

  let session!: Session;
  const socket = new GameSocket(platform, ui, {
    onServer: (msg) => session.handle(msg),
    onOpen: () => session.onConnected(),
    onClose: () => session.onDisconnected(),
  });
  session = new Session(platform, socket, ui);
  session.applyLaunchQuery(platform.getLaunchQuery(), platform.getLaunchScene() ?? undefined);
  platform.onAppHide(() => socket.onBackground());
  platform.onAppShow((query, scene) => {
    socket.onForeground();
    session.applyLaunchQuery(query, scene);
  });
  if (ui.avatarUrl) session.loadSelfAvatar(ui.avatarUrl);
  if (wechat) {
    const afterPrivacyReady = (): void => {
      void platform.fetchAuthorizedUserInfo().then((info) => {
        if (info) session.applyWxProfile(info.nickname, info.avatar);
        else session.tryResumeAfterProfile();
      });
    };
    platform.onPrivacyPrompt(() => {
      if (!ui.privacyAgreed) ui.showPrivacy = true;
    });
    if (ui.privacyAgreed) afterPrivacyReady();
  }
  session.tryResumeAfterProfile();

  const canvas = platform.canvas;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("2d context missing");

  let assets: AssetBank = { images: new Map() };
  void loadAssets(platform).then((a) => {
    assets = a;
  });

  let frames = 0;
  let fpsAt = platform.now();
  let draggingScroll = false;
  let dragStartX = 0;
  let dragStartY = 0;
  let dragScroll0 = 0;
  let onKeyboard = false;
  let moved = 0;
  let lastTickSec = -1;

  const loop = (_t: number): void => {
    const logical = platform.logical;
    const w = logical.w;
    const h = logical.h;
    const dpr = platform.dpr;
    const nextW = Math.max(1, Math.floor(w * dpr));
    const nextH = Math.max(1, Math.floor(h * dpr));
    if (canvas.width !== nextW) canvas.width = nextW;
    if (canvas.height !== nextH) canvas.height = nextH;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    const now = platform.now();
    frames += 1;
    if (now - fpsAt >= 500) {
      ui.fps = (frames * 1000) / Math.max(1, now - fpsAt);
      frames = 0;
      fpsAt = now;
    }
    const inset = platform.safeInset();
    ui.safeTop = inset.top;
    ui.safeBottom = inset.bottom;
    ui.capsuleLeft = inset.capsuleLeft;
    ui.capsuleTop = inset.capsuleTop;
    ui.capsuleHeight = inset.capsuleHeight;
    if (session) session.syncHallRank(ui.screen === "hall" ? hallRankRows(hallHomeLayout(w, h, ui).rank) : null);
    drawFrame(ctx, ui, assets, w, h, now);
    drawDebug(ctx, ui, w, h, now);
    lastTickSec = playCountdownTick(platform, ui, now, lastTickSec);
    syncWxUserInfoButton(platform, ui, session, w, h);
    platform.raf(loop);
  };
  platform.raf(loop);
  platform.playLoopingBgm("assets/bgm.mp3");

  platform.onResize(() => {
    /* logical 已由平台更新 */
  });

  platform.onPointer((ev) => {
    const w = platform.logical.w;
    const h = platform.logical.h;
    ui.pointerX = ev.x;
    ui.pointerY = ev.y;
    if (ev.kind === "down") {
      platform.playLoopingBgm("assets/bgm.mp3");
      ui.pointerDown = true;
      moved = 0;
      dragStartX = ev.x;
      dragStartY = ev.y;
      const bands = battleBands(w, h, ui);
      const overlay = ui.showSettings || ui.showLeaderboard || ui.showPrivacy;
      if (!overlay && ui.screen === "result") {
        draggingScroll = contains(resultLayout(w, h, ui).chain, ev.x, ev.y);
        dragScroll0 = ui.scrollY;
      } else {
        draggingScroll = !overlay && ui.screen === "battle" && contains(bands.scroll, ev.x, ev.y);
        dragScroll0 = ui.scrollX;
        if (draggingScroll) ui.scrollFollow = false;
      }
      onKeyboard = !overlay && ui.screen === "battle" && contains(bands.keys, ev.x, ev.y);
      if (onKeyboard) onKeyDown(ui, bands.keys, ev.x, ev.y, keyboardEnabled(ui));
    } else if (ev.kind === "move") {
      moved = Math.hypot(ev.x - dragStartX, ev.y - dragStartY);
      if (draggingScroll) {
        if (ui.screen === "result") ui.scrollY = Math.max(0, dragScroll0 - (ev.y - dragStartY));
        else ui.scrollX = Math.max(0, dragScroll0 - (ev.x - dragStartX));
      }
      if (onKeyboard) {
        const bands = battleBands(w, h, ui);
        onKeyMove(ui, bands.keys, ev.x, ev.y, keyboardEnabled(ui));
      }
    } else if (ev.kind === "cancel") {
      ui.pointerDown = false;
      draggingScroll = false;
      onKeyboard = false;
      ui.pressedKey = null;
      ui.popup = null;
    } else {
      const bands = battleBands(w, h, ui);
      if (onKeyboard) {
        onKeyUp(ui, session, bands.keys, ev.x, ev.y, keyboardEnabled(ui), () => platform.vibrateShort(), () =>
          platform.playSfx(SFX.key),
        );
      } else if (!draggingScroll || moved < 10) {
        const buttons = collectButtons(ui, w, h);
        const hit = hitButton(buttons, ev.x, ev.y);
        if (hit) {
          if (!handleCandidateTap(session, hit.id)) {
            handleUiTap(ui, session, hit.id, w, h);
            platform.playSfx(SFX.tap);
          }
          platform.vibrateShort();
        }
      }
      ui.pointerDown = false;
      draggingScroll = false;
      onKeyboard = false;
      ui.pressedKey = null;
      ui.popup = null;
    }
  });

  platform.onKey((ev) => {
    if (ev.repeat) return;
    if (ev.key === "d" || ev.key === "D") ui.debug = !ui.debug;
  });

  platform.log("接词大乱斗 client ready", platform.kind, platform.logical);
}

function contains(r: { x: number; y: number; w: number; h: number }, x: number, y: number): boolean {
  return x >= r.x && x <= r.x + r.w && y >= r.y && y <= r.y + r.h;
}

function playCountdownTick(platform: Platform, ui: ReturnType<typeof createState>, now: number, lastSec: number): number {
  let sec = -1;
  if (ui.screen === "private" && ui.lobbyCountdownDeadline > 0) {
    sec = Math.ceil((ui.lobbyCountdownDeadline - serverNow(ui, now)) / 1000);
  } else if (ui.screen === "battle" && isMyTurn(ui) && ui.deadline > 0) {
    sec = Math.ceil(remainingMs(ui, now) / 1000);
  }
  if (sec >= 1 && sec <= 3 && sec !== lastSec) {
    platform.playSfx(SFX.tick);
    return sec;
  }
  if (sec > 3 || sec <= 0) return -1;
  return lastSec;
}

function syncWxUserInfoButton(platform: Platform, ui: ReturnType<typeof createState>, session: Session, w: number, h: number): void {
  if (platform.kind !== "wechat") return;
  if (ui.showPrivacy || !ui.privacyAgreed || ui.screen !== "login" || ui.wxProfileReady || ui.connecting) {
    platform.hideUserInfoButton();
    return;
  }
  const enter = layoutLogin(w, h, ui).find((b) => b.id === "enter");
  if (!enter) {
    platform.hideUserInfoButton();
    return;
  }
  platform.syncUserInfoButton(enter, (info) => {
    if (!info) {
      ui.toast = "需要授权微信头像和昵称才能入座";
      ui.toastUntil = Date.now() + 2200;
      return;
    }
    session.applyWxProfile(info.nickname, info.avatar);
    platform.hideUserInfoButton();
    session.enterTeahouse();
  });
}

boot();
